import { useMemo } from 'react'
import { AuthMenu } from '@/features/auth/AuthMenu'
import { AuthScreen } from '@/features/auth/AuthScreen'
import { ResetScreen } from '@/features/auth/ResetScreen'
import { useAuth } from '@/features/auth/useAuth'
import { FriendsScreen } from '@/features/friends/FriendsScreen'
import { useFriendRequests, useUserChannel } from '@/features/friends/useFriends'
import { GameScreen } from '@/features/game/GameScreen'
import { type Route, useRoute } from '@/features/game/useRoute'
import { LeaderboardScreen } from '@/features/leaderboard/LeaderboardScreen'
import { GearIcon, HelpIcon, TrophyIcon } from '@/features/lobby/icons'
import { LobbyScreen } from '@/features/lobby/LobbyScreen'
import { Modal } from '@/features/lobby/Modal'
import { ProfileScreen } from '@/features/profile/ProfileScreen'
import { RulesScreen } from '@/features/rules/RulesScreen'
import { SettingsScreen } from '@/features/settings/SettingsScreen'
import { ShopScreen } from '@/features/shop/ShopScreen'
import { rememberSecret } from '@/shared/game/storage'

/**
 * Личная ссылка возврата: /g/<id>?p=<секрет>.
 *
 * Секрет живёт в localStorage, а он теряется — другой браузер, приватное
 * окно, чистка. Тогда игрок превращался в зрителя и терял право хода прямо
 * посреди партии. Ссылка с секретом возвращает его обратно. Вошедшему в
 * учётную запись она не нужна: его сервер узнаёт и так.
 *
 * Разбирается при рендере, а не в эффекте: запрос состояния уходит раньше
 * эффектов, и без секрета первый ответ пришёл бы обезличенным.
 *
 * Из адреса секрет сразу убирается — адресную строку копируют, чтобы
 * позвать соперника, и делиться своим удостоверением при этом нельзя.
 */
function useAdoptedSecret(route: Route): void {
  useMemo(() => {
    if (route.name !== 'game') return

    const secret = new URLSearchParams(window.location.search).get('p')

    if (secret === null || secret === '') return

    rememberSecret(route.id, secret)
    window.history.replaceState({}, '', `/g/${route.id}`)
  }, [route])
}

/**
 * Экраны: лобби, партия, вход, друзья и профиль. Партия живёт по адресу
 * /g/<id> — это и есть приглашение, отдельной ссылки-приглашения не нужно.
 */
export default function App() {
  const { route, base, go, back } = useRoute()
  const { me } = useAuth()

  useAdoptedSecret(route)

  // Свой канал: заявки в друзья и приглашения в партию приходят на него.
  useUserChannel(me?.id ?? null)

  // Счётчик заявок в шапке: иначе про них узнаёшь, только зайдя в друзей.
  const requests = useFriendRequests(me !== null)

  /*
   * Игровой экран шире остальных.
   *
   * Доска квадратная и растёт вместе с колонкой, а 64rem страницы упирали её
   * в ~736px: на 2K-мониторе партия занимала четверть экрана, и играть
   * приходилось с браузерным зумом. Остальные экраны — тексты и карточки, им
   * широкая мера только вредит, поэтому потолок поднимается ровно на партии.
   */
  return (
    <main className={`page${base.name === 'game' ? ' page--wide' : ''}`}>
      <header className="page__head">
        <h1 className="page__title">
          {/* Заголовок ведёт на лобби: обычная ссылка, а не кнопка. */}
          <a
            className="brand"
            href="/"
            onClick={(event) => {
              event.preventDefault()
              go('/')
            }}
          >
            {/* Четыре фишки квадратом: игра узнаётся значком, а не словом. */}
            <span className="brand__mark" aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </span>
            {/*
              Слово в своём span, чтобы на узком экране его мог убрать CSS:
              с четвёртой кнопкой в шапке ряд переносился второй строкой.
              Значок игру называет, слово — повторяет.
            */}
            <span className="brand__word">Шешбеш</span>
          </a>
        </h1>

        <div className="row" style={{ marginLeft: 'auto' }}>
          {/*
            Правила есть и в шапке: за ними приходят посреди партии («а
            прыгать-то можно?»), а не только перед первой.
            
            Значком ВСЮДУ, а не подписью на широком экране и значком на узком.
            Пока подпись оставалась, она была единственным текстом в ряду из
            значков — и вставала на другой высоте: у строки и у svg своя
            метрика, и ряд из-за этого выглядел прыгающим. Заодно отпала
            развилка «где подпись, где значок».
            
            Слово никуда не делось: title показывает его подсказкой браузера,
            aria-label читает читалка.
          */}
          <button
            className="ghost"
            type="button"
            onClick={() => go('/rules')}
            aria-label="как играть"
            title="как играть"
          >
            <span className="ghost__icon ghost__icon--always" aria-hidden="true">
              <HelpIcon />
            </span>
          </button>
          {/*
            Лидеры — кубком, между правилами и настройками.

            Значком, как и его соседи: третья подпись в ряду из значков
            снова поставила бы ряд на другую высоту (см. комментарий у
            правил выше). Заглядывают сюда так же, как в правила, — на
            минуту, часто посреди партии, — поэтому и открывается это окном
            поверх экрана, а не отдельным экраном.
          */}
          <button
            className="ghost"
            type="button"
            onClick={() => go('/leaders')}
            aria-label="таблица лидеров"
            title="таблица лидеров"
          >
            <span className="ghost__icon ghost__icon--always" aria-hidden="true">
              <TrophyIcon />
            </span>
          </button>
          {/*
            Настройки — шестерёнкой, и она есть у всех: тема, громкость и вид
            поля нужны и гостю.

            Отдельной кнопки звука рядом больше нет, и убрать её позволило
            именно то, что настройки открываются модальным окном. Держали её
            в шапке по одной причине: гасят звук не заранее, а посреди партии —
            телефон зазвонил в тихой комнате, — и уходить за этим на другой
            экран значило уйти с хода. Уходить больше не надо: окно
            открывается поверх доски и закрывается на месте, а громкость
            в нём не выключателем, а ползунком.

            Всё про учётную запись — под кнопкой с аватаркой, и она последняя:
            это самый правый край шапки, куда большой палец дотягивается
            легче всего.
          */}
          <button
            className="ghost"
            type="button"
            onClick={() => go('/settings')}
            aria-label="настройки"
            title="настройки"
          >
            <span className="ghost__icon ghost__icon--always" aria-hidden="true">
              <GearIcon />
            </span>
          </button>
          <AuthMenu me={me} pending={requests.data?.incoming.length ?? 0} go={go} />
        </div>
      </header>

      {base.name === 'game' && (
        <GameScreen
          // Новая партия из финала — новый экран: выбор фишки, приближение и
          // очередь показа прошлой партии к ней не относятся.
          key={base.id}
          gameId={base.id}
          me={me}
          onLeave={() => go('/')}
          onOpen={(id) => go(`/g/${id}`)}
          onNavigate={go}
        />
      )}
      {base.name === 'auth' && <AuthScreen onDone={() => go('/')} />}
      {base.name === 'reset' && <ResetScreen onDone={() => go('/login')} />}
      {base.name === 'lobby' && (
        <LobbyScreen
          me={me}
          onCreated={(id) => go(`/g/${id}`)}
          onOpen={(id) => go(`/g/${id}`)}
          go={go}
        />
      )}

      {/*
        Накладки — модальным окном ПОВЕРХ экрана, а не вместо него.
        
        Адрес у каждой свой (ссылкой на правила делятся, «назад» окно
        закрывает), но уходить с доски за ними не надо: за правилами приходят
        посреди партии («а прыгать-то можно?»), за громкостью — когда телефон
        зазвонил в тихой комнате, а в профиль и друзей заглядывают на минуту,
        не бросая хода.
      */}
      {route.name === 'rules' && (
        <Modal title="Как играть" onClose={back}>
          <RulesScreen />
        </Modal>
      )}
      {route.name === 'settings' && (
        <Modal title="Настройки" onClose={back}>
          <SettingsScreen />
        </Modal>
      )}
      {route.name === 'friends' && (
        <Modal title="Друзья" onClose={back}>
          <FriendsScreen onOpenGame={(id) => go(`/g/${id}`)} onOpenUser={(id) => go(`/u/${id}`)} />
        </Modal>
      )}
      {route.name === 'leaders' && (
        <Modal title="Лидеры" onClose={back}>
          <LeaderboardScreen
            signedIn={me !== null}
            onSignIn={() => go('/login')}
            onOpenUser={(id) => go(`/u/${id}`)}
          />
        </Modal>
      )}
      {route.name === 'profile' && (
        <Modal title="Профиль" onClose={back}>
          <ProfileScreen userId={null} onOpenGame={(id) => go(`/g/${id}`)} />
        </Modal>
      )}
      {route.name === 'shop' && (
        <Modal title="Магазин" onClose={back}>
          <ShopScreen signedIn={me !== null} onSignIn={() => go('/login')} />
        </Modal>
      )}
      {route.name === 'user' && (
        <Modal title="Профиль" onClose={back}>
          <ProfileScreen userId={route.id} onOpenGame={(id) => go(`/g/${id}`)} />
        </Modal>
      )}
    </main>
  )
}
